import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Plot from 'react-plotly.js';
import { ManText } from '@craft-design-system/core-components-react';
import { Card } from 'react-bootstrap';
import PopoverComponent from '../popover';
import SegmentedControl_totalMileage from './SegmentedControl_totalMileage';
import useSegmentedChartQuery from '../../utils/useSegmentedChartQuery';
import Shimmer_graph from '../shimmer_graph';
import Zoom from '../Zoom';
import DownloadButtonComponent from '../DownloadButtonComponent';


const TotalMileageCard = ({ value }) => {
  const { vin_short, total_mil } = value;

  const [chartType, setChartType] = useState(
    Array.isArray(vin_short) && vin_short.length === 1 ? 'Bar' : 'histogram'
  );

  const [totalMileageChart, setTotalMileageChart] = useState(
    total_mil?.[0] ? JSON.parse(total_mil[0]) : { data: [], layout: {} }
  );
  const [downloadData, setDownloadData] = useState(
    total_mil?.[1] ? JSON.parse(total_mil[1]) : []
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const { fetchSegmentedChart } = useSegmentedChartQuery();

  // Fetch when chartType or VIN changes
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchSegmentedChart({
          vinShort: value.vin_short,
          chartType,
          columnname: 'Max Mileage',
        });

        setTotalMileageChart(JSON.parse(data[0]));   // chart
        setDownloadData(JSON.parse(data[1]));        // download
      } catch (error) {
        console.error('Error fetching chart data:', error);
        setTotalMileageChart({ data: [], layout: {} });
        setDownloadData([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isInitialLoad) {
      fetchData();
    } else {
      setIsInitialLoad(false);
    }
  }, [chartType, JSON.stringify(vin_short)]);

  return (
    <Card className="card_ride">
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
        gap: '8px'
      }}>
        <div style={{
          flex: 1,
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <ManText
            className="card-title-text"
            variant="component-l"
            as="span"
            size="l"
            style={{ lineHeight: 'normal' }}
          >
            Total mileage
          </ManText>
        </div>
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row-reverse'
        }}>
          <PopoverComponent
            id="total_mileage"
            content="<b>Total Driven Distance:</b> This KPI displays the total driven distance of the vehicle since start of data acquisition.<br/><br/><b>Telematic Raw Data:</b><br/>• TBM3: payloaddecoded_totaldrivendistance_value<br/>• CM4: payload_electricmessagesystem_totaldrivendistance<br/><br/><b>Time Series Data:</b><br/>For single vehicles the course of total driven distance since start of data acquisition is displayed.<br/><br/><b>Histogram:</b><br/>Each bar represents the number vehicles that fall within a certain range (bin) of <b>total driven distance values</b>. The latest total driven distance value for the selected vehicle(s) is visualized per default.<br/><br/><b>Boxplot:</b><br/>The boxplot shows the <b>distribution of total driven distance values</b> for the selected vehicle(s). If multiple vehicles are selected, the boxplot reflects the <b>combined distribution</b> of all their latest values.<br/>• The <b>box</b> represents the <b>middle 50%</b> of values (from the 25th percentile ('q1') to the 75th percentile ('q3'))<br/>• The <b>line inside the box</b> shows the <b>median</b> (50th percentile)<br/>• The whiskers extend to show the range of typical values ('min' and 'max') and visualize <b>the spread of the data</b> beyond the central box."
          />
          <Zoom
            chartData={totalMileageChart?.data}
            chartLayout={totalMileageChart?.layout}
            chartTitle="Total mileage"
          />
          <DownloadButtonComponent
            data={downloadData}
            fileName={`total_mileage_${chartType.toLowerCase()}.xlsx`}
          />
        </div>
      </div>

      {/* Segmented control */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <SegmentedControl_totalMileage
          selected={chartType}
          onChange={setChartType}
          vinShort={Array.isArray(vin_short) ? vin_short : [vin_short]}
        />
      </div>

      {/* Chart */}
      {isLoading ? (
        <Shimmer_graph />
      ) : totalMileageChart?.data?.length ? (
        <div style={{ width: '100%', height: '420px' }}>
          <Plot
            data={totalMileageChart.data}
            layout={{
              ...totalMileageChart.layout,
              margin: { t: 10, r: 10, l: 10, b: 130 },
            }}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler
          />
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <ManText
            className="card-title-text"
            variant="component-l"
            as="span"
            size="l"
          >
            No data available
          </ManText>
        </div>
      )}
    </Card>
  );
};

TotalMileageCard.propTypes = {
  value: PropTypes.shape({
    vin_short: PropTypes.oneOfType([PropTypes.array, PropTypes.string]).isRequired,
    total_mil: PropTypes.array.isRequired, // [fig_json, df_json]
  }).isRequired,
};

export default TotalMileageCard;
